import { FC, JSX, useContext, useMemo, useRef, useState } from "react";
import AdvancedExclusionsContextProvider, { AdvancedExclusionsContext } from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import { AdvancedExclusionsWizardContextSteps } from "@app/projects/projection-provider/advanced-exclusions/interfaces";
import { useHistory } from "react-router-dom";
import BulkProjectionSelectProjects from "@app/shared/BulkProjectionSelectProjects";
import BulkExclusionsVerifiedSourcesSelect from "@app/projects/projection-provider/advanced-exclusions/BulkExclusionsVerifiedSourcesSelect";
import { Modal } from "@app/shared/Modals";
import AdvancedExclusionsConfirm from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsConfirm";
import { FlyoutFooter, FlyoutLink } from "@app/shared/Flyouts";
import { ProjectionProviderType } from "@app/projects/enumerators";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";

const AdvancedExclusionsWizard: FC = () => {
  return (
    <AdvancedExclusionsContextProvider>
      <AdvancedExclusionsWizardContent />
    </AdvancedExclusionsContextProvider>
  );
}

const AdvancedExclusionsWizardContent = (): JSX.Element => {
  const wizardContext = useContext(AdvancedExclusionsContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [pageIsValid, setPageIsValid] = useState(false);
  const [modalWarningVisible, setModalWarningVisible] = useState(false);
  const history = useHistory();

  const humanReadablePageNames = {
    [AdvancedExclusionsWizardContextSteps.SelectProject]: "Select Projects",
    [AdvancedExclusionsWizardContextSteps.VerifiedSources]: "Verified Sources",
    [AdvancedExclusionsWizardContextSteps.Confirm]: "Configure"
  };

  const getCurrentStep = () => {
    return wizardContext.page;
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(AdvancedExclusionsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as AdvancedExclusionsWizardContextSteps;
    wizardContext.setPage(AdvancedExclusionsWizardContextSteps[step] as unknown as AdvancedExclusionsWizardContextSteps);
  };

  const goToPreviousPage = () => {
    if (wizardContext.page == AdvancedExclusionsWizardContextSteps.VerifiedSources)
      wizardContext.setPage(AdvancedExclusionsWizardContextSteps.SelectProject);
    else if (wizardContext.page == AdvancedExclusionsWizardContextSteps.Confirm)
      wizardContext.setPage(AdvancedExclusionsWizardContextSteps.VerifiedSources);
  };

  const cancelFlyout = () => {
    history.goBack();
  };

  const validateAndAdvance = () => {
    if (wizardContext.page == AdvancedExclusionsWizardContextSteps.SelectProject) 
      wizardContext.setPage(AdvancedExclusionsWizardContextSteps.VerifiedSources);
    else if (wizardContext.page == AdvancedExclusionsWizardContextSteps.VerifiedSources)
      wizardContext.setPage(AdvancedExclusionsWizardContextSteps.Confirm);
  };

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case AdvancedExclusionsWizardContextSteps.SelectProject:
        return <BulkProjectionSelectProjects
          pageIsValid={setPageIsValid}
        />;
      case AdvancedExclusionsWizardContextSteps.VerifiedSources:
        return <BulkExclusionsVerifiedSourcesSelect
          pageIsValid={setPageIsValid}
        />
      case AdvancedExclusionsWizardContextSteps.Confirm:
        return <AdvancedExclusionsConfirm
          pageIsValid={setPageIsValid}
        />
    }
  }, [wizardContext.page]);

  const handleSubmit = async () => {
    setSubmitClicked(true)
    
    await wizardContext.projectService.createProjectionProviders(
      Number(wizardContext.currentProjectId),
      wizardContext.providerNetworkType,
      ProjectionProviderType.ExcludeImpact,
      wizardContext.exclusionsType.includes("npi"),
      wizardContext.exclusionsType.includes("tin"),
      wizardContext.exclusionsType.includes("contractentity"),
      false,
      wizardContext.selectedExclusionProviders.map((provider) => provider.providerId),
      wizardContext.exclusionReason,
      wizardContext.comment,
      wizardContext.allSpecialties,
      wizardContext.allLocations,
      false, // All projects will always be false
      wizardContext.phone,
      wizardContext.specialtyGroup,
      wizardContext.selectedVerifiedSources,
      wizardContext.selectedExclusionProviders.map((provider) => {
        return {
          contractEntityId: provider.professionalAffiliationId,
          providerId: provider.providerId
        }
      }),
      wizardContext.selectedProjectIds.map(Number),
      wizardContext.runProjectionOnSubmit
      );

    history.goBack();
  }

  const handleBackToProjectClick = () => {
    if (wizardContext.selectedVerifiedSources.length > 0)
    {
      setModalWarningVisible(true);
    }
    else
    {
      history.goBack();
    }
  }

  const getConfirmationSidePanelContent = () => {
    return (
      <>
        <div className="targeting-wizard-flyout">
        </div>
        <FlyoutFooter
          submitButton={
            <QESButton qesType="primary" className="ms-3" onClick={handleSubmit} disabled={submitClicked} qesTestId="advanced-exclusions-wizard__flyout-run">
              {submitClicked ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : "Confirm"}
            </QESButton>
          }
        />
      </>
    );
  };


  return (
    <Page documentTitle="Configurable Exclusions">
      <PageInner className="advanced-exclusions-wizard">
        <Modal
          isVisible={modalWarningVisible}
          closeModal={() => setModalWarningVisible(false)}
          content={{
            body: "Are you sure you want to leave? Unsaved changes will be lost.",
            cancelButtonText: "Cancel",
            confirmButtonAction: history.goBack,
            confirmButtonText: "Confirm",
            confirmButtonType: "primary-danger",
            title: "Warning",
          }}
          hideBodyScroll={true}
          triggerElement={backButtonRef}
        />
        <PageHeader title="Configurable Exclusions">
          <PageHeaderRight>
            <QESButton qesType="primary" onClick={handleBackToProjectClick} qesTestId="advanced-exclusions-wizard__back" qesRef={backButtonRef}>Back to Project</QESButton>
          </PageHeaderRight>
        </PageHeader>

        <br/>
        <WizardSteps currentStep={getCurrentStep()} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />

        <div ref={wizardBodyRef}>
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>

      <WizardNavigationBar
        leftSide={(
          <QESButton qesType="secondary-danger" onClick={cancelFlyout} qesTestId="advanced-exclusions-wizard__cancel">Cancel</QESButton>
        )}

        rightSide={(
          <div className="advanced-exclusions-wizard__nav-buttons">
            {wizardContext.page !== AdvancedExclusionsWizardContextSteps.SelectProject &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="advanced-exclusions-wizard__previous">Previous</QESButton>}
            {wizardContext.page === AdvancedExclusionsWizardContextSteps.Confirm ? (
              <FlyoutLink
                disabled={!pageIsValid}
                content={{
                  body: getConfirmationSidePanelContent(),
                  title: "Are you sure?",
                }}
                classNames="qes-button-primary ms-3"
                testId="advanced-exclusions-submit"
                size="40"
              >
                Complete
              </FlyoutLink>
            ) : (
              <QESButton
                onClick={validateAndAdvance}
                disabled={!pageIsValid}
                  qesTestId="advanced-exclusions-wizard__next"
                className="ms-3">
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
}

export default AdvancedExclusionsWizard;